import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';

import {
  Button,
  Dropdown,
  message,
  Space,
} from 'antd';
import {
  BsDot,
  BsThreeDotsVertical,
} from 'react-icons/bs';
import {
  PiArrowBendDownRightLight,
  PiPencilLight,
  PiTrashLight,
  PiWarningOctagon,
} from 'react-icons/pi';

import CommunityAPI from '../../../../api/CommunityAPI';
import CustomEllipsisText
  from '../../../../components/CustomEllipsisText/CustomEllipsisText';
import ConfirmationModal
  from '../../../../components/Modals/ConfirmationModal/ConfirmationModal';
import RenderMentionText
  from '../../../../components/RenderMentionText/RenderMentionText';
import Report from '../../../../components/Report/Report';
import {
  removeComment,
  setCommentToEdit,
  setReplyToComment,
  updateCommunitComments,
  updateReplyComment,
  userInterationAction,
} from '../../../../context/communityReducer';
import showAppError from '../../../../shared/error';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../shared/hooks';
import {
  IComment,
  ICommentToEdit,
} from '../../../../types/communityTypes';
import {
  Onboarding,
  UAMPermissions,
} from '../../../../types/userTypes';
import LikeButton from '../../../Feed/Posts/PostItem/LikeButton';
import CreatorTag from '../../components/CreatorTag';
import CommunityPostCommentsList from './CommunityPostCommentsList';

type Props = {
  comment: IComment;
  channelId?: string;
  postId?: string;
  parentCommentId?: string;
};

const CommentCard: React.FC<Props> = ({
  comment,
  channelId,
  postId,
  parentCommentId,
}) => {
  const dispatch = useAppDispatch();

  const { communityPostComments } = useAppSelector((state) => state?.community);

  const userDetails = useAppSelector((state) => state?.user);

  const { hostMetadata } = useAppSelector((state) => state?.app);

  const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  const handleViewReply = () => {
    dispatch(updateCommunitComments({ ...comment, viewReply: true }));
  };

  const handleClickLike = useCallback(
    (isLiked: boolean) => {
      if (!(postId && channelId)) return;
      const updatedAttributes = {
        isLiked,
        totalLikes: isLiked ? comment?.totalLikes + 1 : comment?.totalLikes - 1,
      };
      dispatch(
        userInterationAction(
          isLiked ? 'addCommentLike' : 'removeCommentLike',
          postId,
          channelId,
          comment,
          updatedAttributes,
          (updatedComment) =>
            parentCommentId
              ? dispatch(
                  updateReplyComment({
                    parentId: parentCommentId,
                    comment: updatedComment,
                  }),
                )
              : dispatch(updateCommunitComments(updatedComment)),
          comment?._id,
        ),
      );
    },
    [channelId, comment, dispatch, parentCommentId, postId],
  );

  const handelClickOnReply = useCallback(() => {
    if (parentCommentId) {
      const parent = communityPostComments.find(
        (eachComment) => eachComment?._id === parentCommentId,
      );
      dispatch(
        setReplyToComment({
          ...parent,
          parentCommentId,
          replyTo: comment?.commentedBy,
        }),
      );
    } else {
      dispatch(
        setReplyToComment({ ...comment, replyTo: comment?.commentedBy }),
      );
    }
    const input = document.getElementById('community-post-comment-input_id');
    if (input) {
      input.focus();
    }
  }, [comment, communityPostComments, dispatch, parentCommentId]);

  const displayImg = useCallback(() => {
    if (!comment?.images?.length) return null;
    return (
      <div className="comment-card_body_media">
        {comment?.images.map((eachImg) => (
          <img src={eachImg} />
        ))}
      </div>
    );
  }, [comment?.images]);

  const onDeleteMessage = async () => {
    if (!channelId || !postId) return;
    try {
      const resp = await CommunityAPI.removeComment(
        channelId,
        postId,
        comment?._id,
      );
      if (resp.status === 204) {
        dispatch(removeComment(comment?._id, parentCommentId));
      } else {
        showAppError(resp?.data);
      }
    } catch (error) {
      showAppError(error);
    }
  };

  const handleCommentDelete = () => {
    setIsDeleteDialogVisible(true);
  };

  const handleCommentReport = () => {
    setShowReportModal(true);
  };

  const isCreatorOrUAM = useMemo(() => {
    return (
      userDetails?.id === hostMetadata?.creator?._id ||
      userDetails?.userAccessPermissions?.includes(
        UAMPermissions?.COMMUNITY_ACCESS,
      )
    );
  }, [
    userDetails?.id,
    userDetails?.userAccessPermissions,
    hostMetadata?.creator?._id,
  ]);

  const handleCommentEdit = useCallback(() => {
    let commentToEdit = comment as ICommentToEdit;
    if (parentCommentId) {
      commentToEdit = {
        ...commentToEdit,
        isReplay: true,
        parentId: parentCommentId,
      };
    }
    dispatch(setCommentToEdit(commentToEdit));
  }, [comment, dispatch, parentCommentId]);

  const isMyComment = useMemo(() => {
    if (
      comment?.commentedBy?._id === hostMetadata?.creator?._id &&
      userDetails?.type === Onboarding.CREATOR_RESTRICTED
    ) {
      return true;
    }
    return userDetails?.id === comment?.commentedBy?._id;
  }, [
    comment?.commentedBy?._id,
    hostMetadata?.creator?._id,
    userDetails?.id,
    userDetails?.type,
  ]);

  const items = useMemo(() => {
    return [
      {
        key: '1',
        label: (
          <div className=" action-item action-item--danger" key={comment?._id}>
            <button onClick={handleCommentDelete}>
              <div>
                <PiTrashLight />
              </div>
              {isCreatorOrUAM ? 'Delete comment' : 'Remove comment'}
            </button>
          </div>
        ),
      },
    ];
  }, [comment?._id, isCreatorOrUAM]);

  const reportItems = useMemo(() => {
    if (comment.commentedBy?._id === userDetails?.id) return [];
    return [
      {
        key: '2',
        label: (
          <div className=" action-item action-item--danger" key={comment?._id}>
            <button onClick={handleCommentReport}>
              <div>
                <PiWarningOctagon />
              </div>
              Report comment
            </button>
          </div>
        ),
      },
    ];
  }, [comment?._id, comment.commentedBy?._id, userDetails?.id]);

  const editItems = useMemo(() => {
    if (!isMyComment) return [];
    return [
      {
        key: '3',
        label: (
          <div className=" action-item action-item--edit" key={comment?._id}>
            <button onClick={handleCommentEdit}>
              <div>
                <PiPencilLight />
              </div>
              Edit comment
            </button>
          </div>
        ),
      },
    ];
  }, [comment?._id, handleCommentEdit, isMyComment]);

  const displayMenuIcon = useCallback(() => {
    if (isCreatorOrUAM)
      return (
        <Dropdown
          overlayClassName="delete-dropdown"
          menu={{ items: [...editItems, ...items] }}>
          <Space>
            <BsThreeDotsVertical />
          </Space>
        </Dropdown>
      );
    if (!isCreatorOrUAM) {
      return (
        <Dropdown
          overlayClassName="delete-dropdown"
          menu={{ items: [...editItems, ...reportItems] }}>
          <Space>
            <BsThreeDotsVertical />
          </Space>
        </Dropdown>
      );
    }
    return null;
  }, [editItems, isCreatorOrUAM, items, reportItems]);

  return (
    <>
      <div className="comment-card" key={comment?._id} id={comment?._id}>
        <div className="comment-card_avtar">
          <img src={comment?.commentedBy?.profilePicUrl} alt="" />
        </div>
        <div className="comment-card_body">
          <div className="comment-card_body_name">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                width: 'calc(100% - 30px)',
              }}>
              <h4>{comment.commentedBy?.name}</h4>
              {hostMetadata?.creator?._id === comment?.commentedBy?._id ? (
                <CreatorTag />
              ) : null}
              {comment?.isEdited ? (
                <span>
                  <BsDot />
                  Edited
                </span>
              ) : null}
            </div>
            {displayMenuIcon()}
          </div>
          <div className="comment-card_body_comment">
            {/* {comment?.comment} */}
            {/* <Linkify> */}
            {comment?.comment?.trim() ? (
              <CustomEllipsisText
                text={comment?.comment}
                charLimit={178}
                customDisplay={(text) => <RenderMentionText string={text} />}
              />
            ) : null}
            {/* </Linkify> */}
          </div>
          {displayImg()}
          <div className="comment-card_body_action">
            <div className="comment-card_body_action_like">
              <LikeButton
                isLiked={comment?.isLiked}
                like={(like) => handleClickLike(like)}
              />{' '}
              <p>{comment?.totalLikes}</p>
            </div>
            <div className="comment-card_body_action_reply">
              <Button className="siteBtn noStyle" onClick={handelClickOnReply}>
                Reply
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div>
        {comment?.viewReply ? (
          <CommunityPostCommentsList parentCommentId={comment?._id} />
        ) : null}
      </div>
      {comment?.totalReplies && !comment?.viewReply ? (
        <Button
          className="siteBtn noStyle commnet-view-more_button"
          onClick={handleViewReply}>
          <PiArrowBendDownRightLight />{' '}
          <span>View reply({comment?.totalReplies})</span>
        </Button>
      ) : null}
      <ConfirmationModal
        open={isDeleteDialogVisible}
        message={`Do you really want to delete the comment?`}
        title="Delete comment"
        okayButtonText="Delete"
        cancelButtonText="Cancel"
        handleOk={() => {
          onDeleteMessage();
          setIsDeleteDialogVisible(false);
        }}
        handleCancel={() => {
          setIsDeleteDialogVisible(false);
        }}
      />
      <Report
        showModal={showReportModal}
        closeModal={() => setShowReportModal(false)}
        type="channelcomment"
        comment={{
          commentId: comment?._id,
          content: comment?.comment,
          createdAt: comment?.createdAt,
          likeCount: comment?.totalLikes,
          replyCount: comment?.totalReplies,
          userId: comment?.commentedBy?._id,
          name: comment?.commentedBy?.name,
          profilePic: comment?.commentedBy?.profilePicUrl,
          creator: comment?.commentedBy?._id,
          liked: comment?.isLiked,
        }}
        onReportAction={async (data) => {
          const resp = await CommunityAPI.reportCommentOrPost(data);
          if (resp.status === 201) {
            message.success('Comment reported successfully');
            setShowReportModal(false);
          } else {
            showAppError(resp?.data);
          }
        }}
      />
    </>
  );
};

export default CommentCard;
